import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TablePagination,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Chip,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { platformApiFetch } from './platformApi';

const STATUSES = ['active', 'inactive', 'archived'];

function statusColor(s) {
  if (s === 'active') return 'success';
  if (s === 'archived') return 'default';
  return 'warning';
}

export default function PlatformTenantRolesListPage() {
  const navigate = useNavigate();
  const [tenants, setTenants] = useState([]);
  const [qInput, setQInput] = useState('');
  const [appliedQ, setAppliedQ] = useState('');
  const [tenantInput, setTenantInput] = useState('');
  const [appliedTenantId, setAppliedTenantId] = useState('');
  const [statusInput, setStatusInput] = useState('');
  const [appliedStatus, setAppliedStatus] = useState('');
  const [systemInput, setSystemInput] = useState('');
  const [appliedSystem, setAppliedSystem] = useState('');
  const [sort, setSort] = useState('name');
  const [order, setOrder] = useState('asc');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await platformApiFetch('/api/platform/tenants?pageSize=500&sort=tenant_name&order=asc');
        const json = await res.json().catch(() => ({}));
        if (res.ok && !cancelled) setTenants(json.tenants || []);
      } catch (_e) {
        /* ignore */
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const params = new URLSearchParams({
      page: String(page + 1),
      pageSize: String(pageSize),
      sort,
      order
    });
    if (appliedQ) params.set('q', appliedQ);
    if (appliedTenantId) params.set('tenantId', appliedTenantId);
    if (appliedStatus) params.set('status', appliedStatus);
    if (appliedSystem) params.set('system', appliedSystem);
    try {
      const res = await platformApiFetch(`/api/platform/tenant-roles?${params}`);
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error || 'Failed to load roles');
      setData(json);
    } catch (e) {
      setError(e.message);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, appliedQ, appliedTenantId, appliedStatus, appliedSystem, sort, order]);

  useEffect(() => {
    load();
  }, [load]);

  const applyFilters = () => {
    setPage(0);
    setAppliedQ(qInput.trim());
    setAppliedTenantId(tenantInput);
    setAppliedStatus(statusInput);
    setAppliedSystem(systemInput);
  };

  const roles = data?.roles || [];
  const total = data?.pagination?.total ?? 0;

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Tenant roles
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Read-only view of tenant-scoped roles. Changes are made in each tenant&apos;s console.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'flex-end' }}>
          <TextField
            size="small"
            label="Search"
            value={qInput}
            onChange={(e) => setQInput(e.target.value)}
            sx={{ minWidth: 200 }}
          />
          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel>Tenant</InputLabel>
            <Select label="Tenant" value={tenantInput} onChange={(e) => setTenantInput(e.target.value)}>
              <MenuItem value="">All</MenuItem>
              {tenants.map((t) => (
                <MenuItem key={t.id} value={String(t.id)}>
                  {t.tenant_name} ({t.tenant_code})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 130 }}>
            <InputLabel>Status</InputLabel>
            <Select label="Status" value={statusInput} onChange={(e) => setStatusInput(e.target.value)}>
              <MenuItem value="">All</MenuItem>
              {STATUSES.map((s) => (
                <MenuItem key={s} value={s}>
                  {s}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Type</InputLabel>
            <Select label="Type" value={systemInput} onChange={(e) => setSystemInput(e.target.value)}>
              <MenuItem value="">All</MenuItem>
              <MenuItem value="1">System</MenuItem>
              <MenuItem value="0">Custom</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 130 }}>
            <InputLabel>Sort</InputLabel>
            <Select label="Sort" value={sort} onChange={(e) => setSort(e.target.value)}>
              <MenuItem value="name">Name</MenuItem>
              <MenuItem value="slug">Code</MenuItem>
              <MenuItem value="status">Status</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 100 }}>
            <InputLabel>Order</InputLabel>
            <Select label="Order" value={order} onChange={(e) => setOrder(e.target.value)}>
              <MenuItem value="asc">Asc</MenuItem>
              <MenuItem value="desc">Desc</MenuItem>
            </Select>
          </FormControl>
          <Button variant="contained" onClick={applyFilters}>
            Apply
          </Button>
        </Box>
      </Paper>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Paper sx={{ overflow: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Tenant</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Code</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Type</TableCell>
                <TableCell align="right">View</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {roles.map((r) => (
                <TableRow key={r.id} hover>
                  <TableCell>
                    {r.tenant_name}
                    <Typography variant="caption" display="block" color="text.secondary">
                      {r.tenant_code}
                    </Typography>
                  </TableCell>
                  <TableCell>{r.role_name || r.name}</TableCell>
                  <TableCell>
                    <code>{r.role_code || r.slug}</code>
                  </TableCell>
                  <TableCell>
                    <Chip size="small" label={r.status} color={statusColor(r.status)} />
                  </TableCell>
                  <TableCell>
                    {r.is_system_role || r.is_system ? (
                      <Chip size="small" label="System" />
                    ) : (
                      <Chip size="small" label="Custom" variant="outlined" />
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="View">
                      <IconButton size="small" onClick={() => navigate(`/platform/tenant-roles/${r.id}`)}>
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
              {roles.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6}>No roles match your filters.</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={(_e, p) => setPage(p)}
            rowsPerPage={pageSize}
            onRowsPerPageChange={(e) => {
              setPageSize(Number(e.target.value));
              setPage(0);
            }}
            rowsPerPageOptions={[10, 25, 50, 100]}
          />
        </Paper>
      )}
    </Box>
  );
}
